import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import {
  ButtonGroupProps,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { useUser } from 'hooks/useUser';
import { TrackRequest, TrackRequesterPriorityValue } from 'models/TrackRequest';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RequestMessageDialog } from '../dialog/RequestMessageDialog';

export interface VipActionMenuProps extends ButtonGroupProps {
  request: TrackRequest;
  onChangeRequestMessage?: (message: string) => void;
  onChangeRequestPriority?: (value: TrackRequesterPriorityValue) => void;
}

export function VipActionMenu({
  onChangeRequestMessage,
  onChangeRequestPriority,
  request,
  ...rest
}: VipActionMenuProps) {
  const { t } = useTranslation();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | Element>(null);
  const [requestMessageDialogOpen, setRequestMessageDialogOpen] = useState(false);
  const { requesterId } = useUser();
  const openMenu = Boolean(menuAnchorEl);

  const requesterPriority = request.priorities?.find((priority) => priority.id == requesterId);
  const requesterPriorityValue = requesterPriority?.value;

  const handleOpenMenu = (ev: React.MouseEvent<Element>) => {
    ev.preventDefault();
    setMenuAnchorEl(ev.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleRequestMessage = () => {
    setRequestMessageDialogOpen(true);
    handleCloseMenu();
  };

  const handleRequestPriority = (value: TrackRequesterPriorityValue) => {
    onChangeRequestPriority?.(value);
    handleCloseMenu();
  };

  const requesterPriorityItems = [
    {
      icon: <StarBorderIcon fontSize="small" />,
      iconChecked: <StarIcon color="warning" fontSize="small" />,
      checked: requesterPriorityValue === TrackRequesterPriorityValue.High,
      value: TrackRequesterPriorityValue.High,
      label: t('requestPriorityHigh', 'Really want to hear'),
    },
    {
      icon: <ThumbUpOutlinedIcon fontSize="small" />,
      iconChecked: <ThumbUpIcon color="success" fontSize="small" />,
      checked:
        requesterPriorityValue === TrackRequesterPriorityValue.Normal || !requesterPriorityValue,
      value: TrackRequesterPriorityValue.Normal,
      label: t('requestPriorityNormal', 'Would be nice'),
    },
    {
      icon: <ThumbDownOutlinedIcon fontSize="small" />,
      iconChecked: <ThumbDownIcon color="error" fontSize="small" />,
      checked: requesterPriorityValue === TrackRequesterPriorityValue.Low,
      value: TrackRequesterPriorityValue.Low,
      label: t('requestPriorityLow', 'I don’t like this'),
    },
  ];

  return (
    <>
      <IconButton aria-label="settings" onClick={handleOpenMenu} component="a">
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={menuAnchorEl} open={openMenu} onClose={handleCloseMenu}>
        <MenuItem onClick={handleRequestMessage}>
          <ListItemIcon>
            <ModeCommentOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('requestMessageAction', 'Leave a message')}</ListItemText>
        </MenuItem>
        <Divider />
        {requesterPriorityItems.map((item) => (
          <MenuItem
            key={item.value}
            onClick={() => handleRequestPriority(item.value)}
            selected={item.checked}
          >
            <ListItemIcon>{item.checked ? item.iconChecked : item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      <RequestMessageDialog
        request={request}
        open={requestMessageDialogOpen}
        onSave={(message) => {
          onChangeRequestMessage?.(message);
          setRequestMessageDialogOpen(false);
          handleCloseMenu();
        }}
        onClose={() => {
          setRequestMessageDialogOpen(false);
          handleCloseMenu();
        }}
      />
    </>
  );
}
